import { useContext, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import MyTextField from "../../components/inputs/MyTextField";
import MyButton from "../../components/MyButton";
import { SiteContext } from "../../context/SiteProvider";
import { useUser } from "../../hooks/useUser";
import { AuthContext } from "../../context/AuthProvider";
import { useSite } from "../../hooks/useSite";
import MyInputCaptcha from "../../components/inputs/MyInputCaptcha";

const MyPage01 = () => {
  const { setLoadingScreen } = useContext(SiteContext);
  const { profile, setProfile, isAuthenticated } = useContext(AuthContext);
  const { updateInfoMutation } = useUser();
  const queryClient = useQueryClient();
  const { useAuthentication } = useSite();

  const { data: authConfig } = useAuthentication({}, (data) => data.data);

  const initValues = {
    userId: "",
    userName: "",
    email: "",
    phone: "",
    organization: "",
    captchaToken: "",
  };

  const validationSchema = yup.object({
    phone: yup
      .string()
      .required("전화번호는 필수 입력 항목입니다.")
      .matches(/^010-\d{4}-\d{4}$/, "010-1234-5678 형식으로 입력해주세요."),
    captcha: yup.string().required("자동입력방지 인증코드를 입력해주세요."),
  });

  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const captchaToken = methods.watch("captchaToken");

  const showCaptcha = useMemo(() => !!authConfig?.useCaptcha, [authConfig]);

  const onSubmit = (data) => {
    const payload = {
      profile: {
        organization: data.organization,
        phone: data.phone,
      },
    };

    if (showCaptcha) {
      if (!data.captchaToken) {
        methods.setError("captcha", {
          message: "인증을 진행해주세요.",
        });
        return;
      }
      payload.captchaToken = data.captchaToken;
    }

    setLoadingScreen(true);
    updateInfoMutation.mutate(
      { userId: profile.userId, payload },
      {
        onSuccess: (res) => {
          if (res?.id) {
            alert("내 정보 변경이 완료되었습니다.");
            queryClient.invalidateQueries({ queryKey: "/user/{userId}" });
            setProfile(res);
          } else {
            alert("내 정보 변경 중 오류가 발생했습니다.");
          }
        },
        onError: () => {
          alert("내 정보 변경 중 오류가 발생했습니다.");
        },
        onSettled: () => {
          setLoadingScreen(false);
        },
      }
    );
  };

  useEffect(() => {
    if (profile && Object.keys(profile)?.length) {
      methods.setValue("userId", profile?.id);
      methods.setValue("userName", profile?.userName);
      methods.setValue("email", profile?.email);
      methods.setValue("phone", profile?.profile?.phone);
      methods.setValue("organization", profile?.profile?.organization);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  return isAuthenticated ? (
    <div className="flex justify-center">
      <div className="w-full max-w-[600px] pt-6">
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="flex flex-wrap gap-x-[4%] gap-y-5"
          >
            <div className="md:w-[48%] w-full flex flex-col">
              <label htmlFor="userId" className="w-[100px] font-semibold">
                아이디
              </label>
              <div className="flex-1">
                <MyTextField
                  disabled
                  id="userId"
                  name="userId"
                  placeholder="아이디"
                />
              </div>
            </div>
            <div className="md:w-[48%] w-full flex flex-col">
              <label htmlFor="userName" className="w-[100px] font-semibold">
                이름
              </label>
              <div className="flex-1">
                <MyTextField
                  id="userName"
                  name="userName"
                  placeholder="이름"
                  disabled
                />
              </div>
            </div>
            <div className="w-full flex flex-col">
              <label htmlFor="email" className="w-[100px] font-semibold">
                이메일
              </label>
              <div className="flex-1">
                <MyTextField
                  disabled
                  id="email"
                  name="email"
                  placeholder="이메일"
                />
              </div>
            </div>
            <div className="md:w-[48%] w-full flex flex-col">
              <label htmlFor="phone" className="w-[100px] font-semibold">
                연락처
              </label>
              <div className="flex-1">
                <MyTextField id="phone" name="phone" placeholder="연락처" />
              </div>
            </div>
            <div className="md:w-[48%] w-full flex flex-col">
              <label htmlFor="organization" className="w-[100px] font-semibold">
                소속 기관
              </label>
              <div className="flex-1">
                <MyTextField
                  id="organization"
                  name="organization"
                  placeholder="소속 기관"
                />
              </div>
            </div>
            <MyInputCaptcha name="captchaToken" className={"hidden"} />

            <MyButton
              variant="contained"
              color="primary"
              className="w-full font-semibold h-[56px]"
              type="submit"
              size="small"
              disabled={!captchaToken}
            >
              내 정보 변경하기
            </MyButton>
          </form>
        </FormProvider>
      </div>
    </div>
  ) : null;
};

export default MyPage01;
